import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RankingItem } from '../../types';
import { rankingItemsApi } from '../../api';

interface ItemsState {
  items: RankingItem[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ItemsState = {
  items: [],
  status: 'idle',
  error: null,
};

export const fetchItemsByRankingId = createAsyncThunk(
  'items/fetchItemsByRankingId',
  async (rankingId: string) => {
    const response = await rankingItemsApi.getByRankingId(rankingId);
    return response.data;
  }
);

export const createItem = createAsyncThunk(
  'items/createItem',
  async (item: Omit<RankingItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    const response = await rankingItemsApi.create(item);
    return response.data;
  }
);

export const updateItem = createAsyncThunk(
  'items/updateItem',
  async ({ id, item }: { id: string; item: Partial<RankingItem> }) => {
    const response = await rankingItemsApi.update(id, item);
    return response.data;
  }
);

export const deleteItem = createAsyncThunk(
  'items/deleteItem',
  async (id: string) => {
    await rankingItemsApi.delete(id);
    return id;
  }
);

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    reorderItems: (state, action: PayloadAction<string[]>) => {
      // 本地重新排序项目
      const itemIds = action.payload;
      state.items = itemIds.map((id, index) => {
        const item = state.items.find(item => item.id === Number(id));
        if (item) {
          return { ...item, position: index };
        }
        return {} as RankingItem; // 这里应该不会发生，只是为了类型安全
      }).filter(item => item.id) as RankingItem[];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItemsByRankingId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItemsByRankingId.fulfilled, (state, action: PayloadAction<RankingItem[]>) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchItemsByRankingId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '获取榜单项目失败';
      })
      .addCase(createItem.fulfilled, (state, action: PayloadAction<RankingItem>) => {
        state.items.push(action.payload);
      })
      .addCase(updateItem.fulfilled, (state, action: PayloadAction<RankingItem>) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteItem.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter(item => item.id !== Number(action.payload));
      });
  },
});

export const { reorderItems } = itemsSlice.actions;
export default itemsSlice.reducer; 